import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, AlertTriangle, X } from 'lucide-react';
import { useCredits } from '../hooks/useCredits';
import QuickTopUp from './QuickTopUp';

/**
 * CreditNotice — subtle persistent banner that surfaces when the user is low
 * on Metis credits, and a hard alert when they run out. "Buy more credits"
 * launches the PricingModal in-place so the current lesson isn't disrupted.
 */
export default function CreditNotice() {
    const { credits, low, out } = useCredits();
    const [buyOpen, setBuyOpen] = useState(false);
    const [dismissedLow, setDismissedLow] = useState(false);

    const showLow = low && !out && !dismissedLow;

    return createPortal(
        <>
            <AnimatePresence>
                {showLow && (
                    <motion.div
                        key="low"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.3 }}
                        className="fixed top-4 right-4 z-[310] max-w-sm w-[calc(100vw-2rem)] rounded-lg shadow-2xl border border-[#B79CFF]/50"
                        style={{
                            background:
                                'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(245,240,255,0.98) 100%)',
                            backdropFilter: 'blur(14px)',
                            willChange: 'transform, opacity',
                        }}
                        data-testid="credit-notice-low"
                    >
                        <div className="flex items-start gap-3 p-3 sm:p-4">
                            <div
                                className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: 'var(--lavender)' }}
                            >
                                <Zap size={16} className="text-[color:var(--moss-dark)]" fill="currentColor" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="mono text-[9px] tracking-[0.2em] text-[#7A5FCB]">
                                    METIS · LOW CREDITS
                                </div>
                                <div className="text-[13px] text-[color:var(--ink)] mt-0.5" data-testid="credit-notice-message">
                                    {credits} {credits === 1 ? 'credit' : 'credits'} left — top up to keep learning.
                                </div>
                                <button
                                    onClick={() => setBuyOpen(true)}
                                    data-testid="credit-buy-more"
                                    className="mt-2 inline-flex items-center gap-1 mono text-[11px] font-semibold text-[color:var(--moss-dark)] px-3 py-1.5 rounded-sm"
                                    style={{ background: 'var(--lavender)' }}
                                >
                                    <Sparkles size={11} /> Buy more credits
                                </button>
                            </div>
                            <button
                                onClick={() => setDismissedLow(true)}
                                data-testid="credit-notice-dismiss"
                                className="text-[color:var(--moss-deep)]/60 hover:text-[color:var(--moss-deep)] flex-shrink-0"
                                aria-label="Dismiss"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {out && (
                    <motion.div
                        key="out"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[320] flex items-center justify-center p-4"
                        style={{ background: 'rgba(11,15,7,0.7)', backdropFilter: 'blur(4px)' }}
                        data-testid="credit-notice-out"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            className="w-full max-w-md rounded-lg overflow-hidden"
                            style={{
                                background:
                                    'linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(245,240,255,0.98) 100%)',
                                border: '1px solid rgba(183,156,255,0.6)',
                                boxShadow: '0 30px 70px -20px rgba(11,15,7,0.6)',
                            }}
                        >
                            <div className="p-6 text-center">
                                <div
                                    className="h-14 w-14 mx-auto rounded-full flex items-center justify-center mb-3"
                                    style={{ background: 'var(--lavender)' }}
                                >
                                    <AlertTriangle size={22} className="text-[color:var(--moss-dark)]" />
                                </div>
                                <div className="display text-2xl text-[color:var(--ink)]" data-testid="credit-out-title">
                                    You're out of Metis credits.
                                </div>
                                <div className="text-[13px] text-[color:var(--ink-soft)] mt-2">
                                    Top up to continue with lessons, quizzes, and voice chats.
                                    Your current lesson is saved — you'll pick up right where you left off.
                                </div>
                                <button
                                    onClick={() => setBuyOpen(true)}
                                    data-testid="credit-out-buy"
                                    className="mt-4 inline-flex items-center gap-2 mono text-[12px] font-semibold text-[color:var(--moss-dark)] px-5 py-3 rounded-sm"
                                    style={{ background: 'var(--lavender)' }}
                                >
                                    <Sparkles size={13} /> Buy more credits
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <QuickTopUp open={buyOpen} onClose={() => setBuyOpen(false)} />
        </>,
        document.body,
    );
}
